import React, { useEffect, useRef, useState } from "react";
import { ArrowUpFromLineIcon } from "lucide-react";

const ScrollTop = ({ threshold = 200, duration = 600, bottom = 24, right = 24 }) => {
  const [visible, setVisible] = useState(false);
  const rafRef = useRef(null);
  const isAnimatingRef = useRef(false);

  // Show/hide button based on scroll position
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onScroll = () => {
      const y = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
      setVisible(y > threshold);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll(); // initial check
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);

  // Cancel running animation when user interacts (prevents "auto-scroll locking")
  useEffect(() => {
    const cancelAnimation = () => {
      if (isAnimatingRef.current) {
        isAnimatingRef.current = false;
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };

    window.addEventListener("wheel", cancelAnimation, { passive: true });
    window.addEventListener("touchstart", cancelAnimation, { passive: true });
    window.addEventListener("keydown", cancelAnimation, { passive: true });

    return () => {
      window.removeEventListener("wheel", cancelAnimation);
      window.removeEventListener("touchstart", cancelAnimation);
      window.removeEventListener("keydown", cancelAnimation);
    };
  }, []);

  // Easing helper (smooth, pleasant)
  const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

  // Smooth scroll implementation with cancelation support
  const scrollToTop = () => {
    const el = document.scrollingElement || document.documentElement || document.body;
    const start = el.scrollTop || window.pageYOffset || 0;
    if (!start) return;

    const startTime = performance.now();
    isAnimatingRef.current = true;

    const step = (now) => {
      if (!isAnimatingRef.current) return; // stop if canceled
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeInOutCubic(progress);
      const current = Math.round(start * (1 - eased));

      // Use window.scrollTo for best cross-browser behavior
      window.scrollTo(0, current);

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        isAnimatingRef.current = false;
        rafRef.current = null;
      }
    };

    // cancel any previous animation first
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    rafRef.current = requestAnimationFrame(step);
  };

  if (!visible) return null;

  return (
    <button onClick={scrollToTop} aria-label="Scroll to top" style={{ position: "fixed", bottom: `${bottom}px`, right: `${right}px`, zIndex: 9999 }} className="!p-2 cursor-pointer rounded-full bg-[#008080] hover:bg-[#007070] text-white shadow-lg transform transition-transform duration-200 hover:scale-110">
      <ArrowUpFromLineIcon size={20} />
    </button>
  );
};

export default ScrollTop;
